import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Info, XCircle, CheckCircle2, ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  addAcceptedPolicy,
  updateAcceptedPolicy,
} from "../../redux/acknowledgments/acknowledgmentsActions.js";
import errorSound from "../../assets/error-010-206498.mp3";
import api from "../../api.js";
import { use } from "react";
import { arClass } from "../../utils/useArClass.js";

const EditWarningMessage = ({ theme, onConfirm, onClose }) => {
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const [shake, setShake] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  const dispatch = useDispatch();

  // Get Redux state for this acknowledgment
 const acceptedPolicies =
  useSelector((state) => state.acknowledgments.acceptedPolicies) ?? [];

const isConfirmed = acceptedPolicies.some(
  (p) => p.policyId === 2 && p.accepted === true
);

const isPolicyInRedux = acceptedPolicies.some(
  (p) => p.policyId === 2
);


  // Trigger vibrate
  const triggerVibration = () => {
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100, 50, 100]);
    }
  };

  // Close logic
  const handleClose = () => {
    if (isConfirmed) {
      onClose();
      return;
    }

    // Shake + sound
    setShake(true);
    setTimeout(() => setShake(false), 600);
    triggerVibration();

    try {
      audioRef.current = new Audio(errorSound);
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch(() => {});
    } catch {}

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        try {
          audioRef.current.currentTime = 0;
        } catch {}
      }
    };
  };

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const res = await api.get("policies/1");
        if (res.data.success) {
          setTerms(res.data.policy);
        }
        setLoading(false);
      } catch (err) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // When user checks the box, call handleAcceptTerms (accept/decline)
 
  const handleAcceptTerms = async (op = "accept") => {
    if (op === "accept") {
      try {
        setLoading(true);
        await api.post("policies/accept", { policyId: 2, accepted: true });

        if (!isPolicyInRedux) {
          dispatch(
            addAcceptedPolicy({
              policyId: 2, // matches policyDetails.id
              accepted: true,
              acceptedAt: new Date().toISOString(),
            })
          );
        } else {
          dispatch(
            updateAcceptedPolicy({
              policyId: 2, // matches policyDetails.id
              accepted: true,
              acceptedAt: new Date().toISOString(),
            })
          );
        }
        setLoading(false);
        onConfirm && onConfirm();
        onClose && onClose();
      } catch (err) {
        setLoading(false);
      // Handle error silently
      }
    } else {
      try {
        setLoading(true);
        await api.post("policies/accept", { policyId: 2, accepted: false });

        if (!isPolicyInRedux) {
          dispatch(
            addAcceptedPolicy({
              policyId: 2, // matches policyDetails.id
              accepted: false,
              acceptedAt: new Date().toISOString(),
            })
          );
        } else {
          dispatch(
            updateAcceptedPolicy({
              policyId: 2, // matches policyDetails.id
              accepted: false,
              acceptedAt: new Date().toISOString(),
            })
          );
        }
        setLoading(false);
        onClose && onClose();
      } catch (err) {
        setLoading(false);
        // Handle error silently
      }
    }
  };


  if (loading || !terms) {
    // Skeleton placeholder that mirrors the real UI
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{
          opacity: 1,
          y: 0,
          ...(shake && {
            x: [0, -10, 10, -10, 10, -5, 5, 0],
            rotate: [0, -1, 1, -1, 1, -0.5, 0.5, 0],
          }),
        }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{
          duration: 0.3,
          ...(shake && {
            x: { duration: 0.3, ease: "easeInOut" },
            rotate: { duration: 0.1, ease: "easeInOut" },
          }),
        }}
        className={`p-3 sm:p-4 rounded-xl border-2 relative ${
          theme.darkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        } transition-colors duration-300`}
        aria-busy={true}
      >
        <div className={arClass("flex flex-col sm:flex-row sm:items-start gap-3 pr-4 sm:pr-10") }>
          {/* Left icon skeleton */}
          <div className={arClass(`w-12 h-12 rounded-lg flex-shrink-0 animate-pulse ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />

          {/* Content skeleton */}
          <div className={arClass("flex-1 min-w-0 space-y-3") }>
            <div className={arClass(`h-5 w-48 max-sm:w-28 rounded animate-pulse ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />
            <div className={arClass(`h-4 w-72 max-sm:w-48 rounded animate-pulse ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />
            <div className={arClass(`h-3 w-40 max-sm:w-24 rounded animate-pulse ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />

            {/* Instructions skeleton */}
            <div className={arClass("mt-3 p-3 rounded-lg border bg-transparent") }>
              <div className={arClass("space-y-2") }>
                {[...Array(4)].map((_, i) => (
                  <div key={i} className={arClass(`h-3 rounded animate-pulse w-full ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />
                ))}
              </div>
            </div>

            {/* Checkbox / button skeletons */}
            <div className={arClass("flex items-center gap-3 mt-3") }>
              <div className={arClass(`w-6 h-6 rounded animate-pulse ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />
              <div className={arClass(`h-4 w-64 rounded animate-pulse ${theme.darkMode ? "bg-gray-700" : "bg-gray-200"}`)} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Instructions derived from fetched policy terms (fallbacks provided)
  const rules = terms?.content[defaultLanguage]?.rules;
  const guidelines = terms?.content[defaultLanguage]?.guidelines;

  const example = terms?.content[defaultLanguage]?.example;
  const impactNotice = terms?.content[defaultLanguage]?.impactNotice;

  // Combine into a single list for rendering
  const instructions = terms
    ? [
        ...rules?.map((r, i) => ({
          title:
            defaultLanguage === "en"
              ? `Rule ${i + 1}`
              : defaultLanguage === "fr"
              ? `Règle ${i + 1}`
              : `قاعدة ${i + 1}`,
          description: r,
        })),
        ...guidelines?.map((g, i) => ({
          title:
            defaultLanguage === "en"
              ? `Guideline ${i + 1}`
              : defaultLanguage === "fr"
              ? `Ligne directrice ${i + 1}`
              : `دليل ${i + 1}`,
          description: g,
        })),
      ]
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{
        opacity: 1,
        y: 0,
        ...(shake && {
          x: [0, -10, 10, -10, 10, -5, 5, 0],
          rotate: [0, -1, 1, -1, 1, -0.5, 0.5, 0],
        }),
      }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{
        duration: 0.3,
        ...(shake && {
          x: { duration: 0.3, ease: "easeInOut" },
          rotate: { duration: 0.1, ease: "easeInOut" },
        }),
      }}
      className={`p-3 sm:p-4 rounded-xl border-2 relative ${
        shake
          ? theme.darkMode
            ? "border-red-500/50 bg-red-500/10"
            : "border-red-400/50 bg-red-50"
          : theme.darkMode
          ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
          : "bg-amber-50 border-amber-200 text-amber-800"
      } transition-colors duration-300`}
    >
      {/* Close button */}
      <motion.button
        onClick={handleClose}
        whileHover={{ scale: isConfirmed ? 1.1 : 1.05 }}
        whileTap={{ scale: 0.9 }}
        animate={{
          scale: shake ? [1, 1.2, 1, 1.2, 1] : 1,
        }}
        transition={{ duration: shake ? 0.6 : 0.2 }}
        className={`absolute top-2 right-2 sm:top-3 sm:right-3 rounded-full shadow-xl ${
          !isConfirmed && shake
            ? "text-red-500"
            : theme.darkMode
            ? "hover:text-amber-400 text-amber-300"
            : "hover:text-amber-900 text-amber-600"
        } transition-colors duration-200 ${
          !isConfirmed ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <XCircle className="w-6 h-6 sm:w-7 sm:h-7" />
      </motion.button>

      <div className="flex flex-col sm:flex-row sm:items-start gap-3 pr-8 sm:pr-10">
        {/* Content container */}
        <div className={arClass("flex-1 min-w-0")}>
          <h4 className={arClass("font-semibold text-sm sm:text-lg mb-2")}>
            {defaultLanguage === "en"
              ? "Data Modification Policy"
              : defaultLanguage === "fr"
              ? "Politique de modification des données"
              : "سياسة تعديل البيانات"}
          </h4>

          <p
            className={arClass(
              "text-xs sm:text-base leading-relaxed flex gap-1"
            )}
          >
            <strong>{terms?.content[defaultLanguage]?.rules?.[1]}</strong>{" "}
          </p>
          <span>{terms?.content[defaultLanguage]?.summary}</span>
          <span>{terms?.content[defaultLanguage]?.impactNotice}</span>

          {/* Instructions Dropdown */}
          <motion.div className={arClass("mt-4 sm:mt-5")}>
            <motion.button
              onClick={() => setShowInstructions(!showInstructions)}
              className={arClass(
                `flex items-center gap-2 w-full text-xs sm:text-base font-medium p-2 sm:p-3 rounded-lg max-sm:items-start ${
                  theme.darkMode
                    ? "hover:bg-amber-500/20"
                    : "hover:bg-amber-100"
                } transition-colors duration-200`
              )}
              whileTap={{ scale: 0.98 }}
            >
              <ChevronDown
                className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 ${
                  showInstructions ? "rotate-180" : ""
                }`}
              />
              <div
                className={arClass(
                  "flex-1 text-xs sm:text-sm flex items-center gap-1 max-sm:flex-col max-sm:items-start"
                )}
              >
                <span className={arClass("font-medium text-start")}>
                  {defaultLanguage === "en"
                  ? "Why should I only update incorrect entries?"
                  : defaultLanguage === "fr"
                  ? "Pourquoi ne dois-je mettre à jour que les entrées incorrectes ?"
                  : "لماذا يجب عليّ تحديث الإدخالات غير الصحيحة فقط؟"}
                </span>
                <div
                  className={arClass(
                    "text-xs opacity-70 mt-1 sm:mt-0 flex gap-1"
                  )}
                >
                  <span className={arClass("")}>
                    {showInstructions
                      ? defaultLanguage === "en"
                        ? "Hide"
                        : defaultLanguage === "fr"
                        ? "Cacher"
                        : "إخفاء"
                      : defaultLanguage === "en"
                      ? "Show"
                      : defaultLanguage === "fr"
                      ? "Montrer"
                      : "عرض"}
                  </span>
                  <span className={arClass("")}>
                    {defaultLanguage === "en"
                      ? "instructions"
                      : defaultLanguage === "fr"
                      ? "les instructions"
                      : "التعليمات"}
                  </span>
                </div>
              </div>
            </motion.button>

            <AnimatePresence>
              {showInstructions && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden mt-2"
                >
                  <div
                    className={arClass(
                      `p-3 sm:p-4 rounded-lg border ${
                        theme.darkMode
                          ? "bg-gray-800/50 border-amber-500/30"
                          : "bg-white border-amber-200"
                      }`
                    )}
                  >
                    <ul className={arClass("space-y-3 sm:space-y-4")}>
                      {instructions.map((instruction, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={arClass("flex items-start gap-3")}
                        >
                          <div
                            className={arClass(
                              `mt-0.5 p-1 rounded flex-shrink-0 ${
                                theme.darkMode
                                  ? "bg-amber-500/20 text-amber-300"
                                  : "bg-amber-100 text-amber-600"
                              }`
                            )}
                          >
                            <CheckCircle2
                              className={arClass("w-3 h-3 sm:w-4 sm:h-4")}
                            />
                          </div>
                          <div className={arClass("min-w-0")}>
                            <span
                              className={arClass(
                                "font-medium text-xs sm:text-base"
                              )}
                            >
                              {instruction.title}:
                            </span>
                            <p
                              className={arClass(
                                "text-xs sm:text-sm opacity-90 mt-0.5"
                              )}
                            >
                              {instruction.description}
                            </p>
                          </div>
                        </motion.li>
                      ))}
                    </ul>

                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className={arClass(
                        `mt-4 p-3 rounded text-xs sm:text-sm ${
                          theme.darkMode
                            ? "bg-amber-500/10 border border-amber-500/20"
                            : "bg-amber-50 border border-amber-200"
                        }`
                      )}
                    >
                      <strong>
                        {defaultLanguage === "en"
                          ? "Impact:"
                          : defaultLanguage === "fr"
                          ? "Impact :"
                          : "التأثير:"}
                      </strong>{" "}
                      {impactNotice}
                      {example && (
                        <div
                          className={arClass(
                            "mt-2 text-xs sm:text-sm italic opacity-90"
                          )}
                        >
                          <strong>
                            {defaultLanguage === "en"
                              ? "Example:"
                              : defaultLanguage === "fr"
                              ? "Exemple :"
                              : "مثال:"}
                          </strong>
                          {example}
                        </div>
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Checkbox */}
          <motion.div
            className={arClass(
              `mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg border ${
                shake ? "border-red-300/50" : "border-amber-300/30"
              } ${theme.darkMode ? "bg-black/20" : "bg-white/80"}`
            )}
            animate={{
              scale: shake ? [1, 1.02, 1] : 1,
            }}
          >
            <label className={arClass("flex items-start gap-3 cursor-pointer")}>
              <div className={arClass("relative flex-shrink-0 mt-0.5")}>
                <button
                  type="button"
                  onClick={()=>handleAcceptTerms(isConfirmed ? "decline" : "accept")}
                  className={arClass(
                    `w-5 h-5 sm:w-6 sm:h-6 rounded border flex items-center justify-center transition-colors duration-200 ${
                      isConfirmed
                        ? theme.darkMode
                          ? "bg-emerald-500 border-emerald-400"
                          : "bg-emerald-600 border-emerald-700"
                        : theme.darkMode
                        ? "bg-gray-800 border-gray-600"
                        : "bg-white border-gray-300"
                    }`
                  )}>
                  {isConfirmed && (
                    <CheckCircle2
                      className={arClass(
                        "w-4 h-4 sm:w-5 sm:h-5 text-white"
                      )}
                    />
                  )}
                </button>

              </div>

              <span
                className={arClass(
                  "text-xs sm:text-base font-medium leading-relaxed"
                )}
              >
                {defaultLanguage === "en"
                  ? "I acknowledge that I have read and understood the edit warning and agree to make changes only to correct inaccurate entries."
                  : defaultLanguage === "fr"
                  ? "Je reconnais avoir lu et compris l'avertissement de modification et accepte d'apporter des modifications uniquement pour corriger les entrées inexactes."
                  : "أقر بأنني قد قرأت وفهمت تحذير التعديل وأوافق على إجراء تغييرات لتصحيح الإدخالات غير الدقيقة فقط."}
              </span>
            </label>

            {/* Status message */}
            {isConfirmed ? (
              <div
                className={arClass(
                  `flex items-center gap-2 mt-3 text-xs sm:text-sm ${theme.darkMode ? "text-emerald-400" : "text-emerald-600"}`
                )}
              >
                <CheckCircle2
                  className={arClass("w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0")}
                />
                <span className={arClass("text-xs sm:text-sm")}>
                  {defaultLanguage === "en"
                    ? "You have acknowledged the edit warning."
                    : defaultLanguage === "fr"
                    ? "Vous avez reconnu l'avertissement de modification."
                    : "لقد قمت بالاعتراف بتحذير التعديل."}
                </span>
              </div>
            ) : shake ? (
              <div
                className={arClass(
                  `flex items-center gap-2 mt-3 text-xs sm:text-sm font-semibold ${theme.darkMode ? "text-red-400" : "text-red-600"}`
                )}
              >
                <XCircle
                  className={arClass("w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0")}
                />
                <span className={arClass("text-xs sm:text-sm")}>
                  {defaultLanguage === "en"
                    ? "Please confirm before closing!"
                    : defaultLanguage === "fr"
                    ? "Veuillez confirmer avant de fermer !"
                    : "يرجى التأكيد قبل الإغلاق!"}
                </span>
              </div>
            ) : null}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default EditWarningMessage;
